import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import type { ColumnsConfig } from '@components/Common/Table/cardFields';
import { formatDate } from '@helpers/date';
import { humanizeEnum } from '@helpers/labels';
import { Box, Button } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { formatPay, jobLocation, JOBS_PAGE_SIZE, type SourceJob } from './sourceJobs';
import './index.scss';

/**
 * A source's fetched jobs, for review before it is approved (owner, 2026-10-07: "Where can I review
 * those jobs in CC?"). Read from the service through /api/external-jobs/sources/:id/jobs; CLOSED
 * jobs are left out by the service.
 *
 * It was the body of a drawer over the Sources list; it is the Jobs tab of the source's workspace
 * now. Nothing here changes what candidates see: the backend shows a source's jobs only once the
 * source is ACTIVE.
 */

const COLUMNS: ColumnsConfig = {
  title: { card: 'title', tablet: true },
  status: { card: 'status', tablet: true },
  location: { card: 'subtitle', tablet: true },
  pay: { card: 'fact', tablet: true },
  posted: { card: 'fact' },
};

export interface SourceJobsTableProps {
  sourceId: string;
  /** Offer "Run now" on an empty list (the same rule as the row's Run action). */
  canRun: boolean;
  onRun: () => void;
  /** Reports the total once known, so the tab and the rail can show it. */
  onTotal?: (total: number) => void;
}

const SourceJobsTable: React.FC<SourceJobsTableProps> = ({ sourceId, canRun, onRun, onTotal }) => {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<SourceJob[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  // A different source starts again at its first page, with nothing of the last one showing.
  useEffect(() => {
    setPage(0);
    setRows([]);
    setTotal(null);
  }, [sourceId]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setFailed(false);
    axios
      .get(`/api/external-jobs/sources/${encodeURIComponent(sourceId)}/jobs`, { params: { page, size: JOBS_PAGE_SIZE } })
      .then((r) => {
        if (!live) return;
        const n = r.data?.totalElements ?? 0;
        setRows(r.data?.content ?? []);
        setTotal(n);
        onTotal?.(n);
      })
      .catch(() => live && setFailed(true))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // onTotal is a report, not an input: a new function identity must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, page]);

  if (failed) return <EmptyState compact title="Jobs could not be loaded" body="Try again in a moment." />;
  if ((loading || total === null) && rows.length === 0) return <SkeletonLoader rows={6} columns={5} />;
  if (rows.length === 0) {
    return (
      <EmptyState
        compact
        title="No jobs fetched yet. Run it first."
        body="A run reads the careers site and lists what it finds here. Nothing is shown to candidates until the source is approved."
        actions={
          canRun ? (
            <Button variant="outlined" onClick={onRun}>
              Run now
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <MintlyTable
      pagination={{
        enabled: (total ?? 0) > JOBS_PAGE_SIZE,
        page,
        rowsPerPage: JOBS_PAGE_SIZE,
        totalNumberOfRows: total ?? 0,
        handleChangePage: (_e, n) => setPage(n),
      }}
      rows={rows}
      getRowKey={(j: SourceJob) => j.externalJobId}
      columns={COLUMNS}
      selectors={['title', 'status', 'location', 'pay', 'posted']}
      tableHeadRow={{ title: <>Job</>, status: <>Status</>, location: <>Location</>, pay: <>Pay</>, posted: <>Posted</> }}
      tableBodyRow={{
        title: (j: SourceJob) => {
          const href = j.sourceUrl || j.applyUrl;
          return (
            <Box className="ej-source">
              {href ? (
                <a href={href} target="_blank" rel="noopener noreferrer" className="ej-source-name">
                  {j.title || 'Untitled'}
                </a>
              ) : (
                <span className="ej-source-name">{j.title || 'Untitled'}</span>
              )}
              {!!j.skills?.length && <span className="ej-mono">{j.skills.join(', ')}</span>}
            </Box>
          );
        },
        status: (j: SourceJob) => (
          <Box className="ej-source">
            {/* No chip for a job with no status: inventing ACTIVE would misreport it. */}
            {j.status ? <CRMStatusChip status={j.status} /> : null}
            <span className="ej-mono">
              {j.status === 'FILTERED' && j.filterReason
                ? `Filtered: ${humanizeEnum(j.filterReason)}`
                : `Enrichment: ${humanizeEnum(j.enrichmentStatus, 'unknown').toLowerCase()}`}
            </span>
          </Box>
        ),
        location: (j: SourceJob) => <span className="ej-wrap">{jobLocation(j)}</span>,
        pay: (j: SourceJob) => (
          <span className="ej-wrap" title={j.pay?.text || undefined}>
            {formatPay(j.pay)}
          </span>
        ),
        posted: (j: SourceJob) => <>{j.postedAt ? formatDate(j.postedAt) : `Seen ${formatDate(j.firstSeenAt)}`}</>,
      }}
    />
  );
};

export default SourceJobsTable;
